import { useMemo } from 'react'

import { useTenantsQuery } from '~/hooks/services/servicenow/queries/useTenantsQuery'
import useEmissionsQuery from '~/hooks/services/sustainability/queries/useEmissionsQuery'

import { FcFactory } from 'react-icons/fc'
import { useNavigate } from 'react-router'

import { Tile, TileHeader } from '../Tile'

const AzureTotalEmissionsTile = () => {
  const navigate = useNavigate()
  const data = useTenantsQuery()
  const subscriptionIds = useMemo(() => data.data?.flatMap((tenant) => tenant.subscriptions.map((subscription) => subscription.id)) ?? [], [data.data])

  const [months, dateRange] = useMemo(() => {
    const cutoffDate = new Date()
    cutoffDate.setHours(0, 0, 0, 0)
    cutoffDate.setDate(1)
    cutoffDate.setMonth(cutoffDate.getMonth() - 2)

    const startOfYear = new Date(cutoffDate.getFullYear(), 0, 1)
    const months = Math.ceil((cutoffDate.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24 * 30)) + 3

    return [months, { startDate: startOfYear, endDate: cutoffDate }]
  }, [])

  const { data: emissions, isLoading } = useEmissionsQuery({
    subscriptionIds,
    months
  })

  const total = useMemo(() => {
    const timezoneAdjustedEmissions = emissions?.map((e) => {
      const date = new Date(e.date ?? '')
      date.setHours(date.getHours() + date.getTimezoneOffset() / 60)
      return { ...e, parsedDate: date }
    })

    const dateFilteredEmissions = timezoneAdjustedEmissions?.filter((e) => {
      const parsedDate = new Date(e.parsedDate ?? '')
      return parsedDate >= dateRange.startDate && parsedDate <= dateRange.endDate
    })

    return dateFilteredEmissions?.reduce((acc, curr) => acc + (curr.scope1 ?? 0) + (curr.scope2 ?? 0) + (curr.scope3 ?? 0), 0) ?? 0
  }, [emissions, dateRange])

  // Format the time period string
  const timePeriodText = useMemo(() => {
    const startMonth = dateRange.startDate.toLocaleString('default', { month: 'short' })
    const startYear = dateRange.startDate.getFullYear()
    const endMonth = dateRange.endDate.toLocaleString('default', { month: 'short' })
    const endYear = dateRange.endDate.getFullYear()

    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`
  }, [dateRange])

  return (
    <Tile onMouseDown={() => navigate('/azure_emissions')}>
      <TileHeader loading={isLoading}>
        <TileHeader.Title className="flex items-center gap-2">
          {total === 0 ? 0 : total.toFixed(4)}
          <div className="mt-1 text-xs font-medium text-th-text-secondary">(kg CO₂e)</div>
        </TileHeader.Title>
        <TileHeader.Subtitle>Total Emissions (Year to Date)</TileHeader.Subtitle>
        <TileHeader.Item>
          <FcFactory className="h-10 w-10" />
        </TileHeader.Item>
        <div className="mt-1 text-xs text-th-text-secondary">{timePeriodText}</div>
      </TileHeader>
    </Tile>
  )
}

export default AzureTotalEmissionsTile
